import { useEffect, useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import ConfigForm from "../components/ConfigForm";
import { useEngine } from "../context/EngineContext";

const TRANSFORMER_EVENT = "transformerValidationFailed";

function validateTransformerConfig(config) {
  const orientationDisabled =
    config?.mountType === "single_axis" || config?.mountType === "dual_axis";
  const orientation = config?.panelOrientation || {};
  const tilt = orientation.tilt;
  const azim = orientation.azim;

  const isInvalidNumber = (value) =>
    value === null ||
    value === undefined ||
    value === "" ||
    !Number.isFinite(Number(value));

  if (!config?.hasTransformer) {
    return {
      hasMissing:
        !orientationDisabled && (isInvalidNumber(tilt) || isInvalidNumber(azim)),
      missing: {
        tilt: !orientationDisabled && isInvalidNumber(tilt),
        azim: !orientationDisabled && isInvalidNumber(azim),
      },
    };
  }

  const details = config.transformerDets || {};

  const loadLoss = details.loadLoss ?? details.load;
  const noLoadLoss = details.noLoadLoss ?? details.noLoad;
  const rated = details.rated ?? details.ratedPower;

  const isInvalidPositiveNumber = (value) =>
    isInvalidNumber(value) || Number(value) <= 0;

  const missing = {
    tilt: !orientationDisabled && isInvalidNumber(tilt),
    azim: !orientationDisabled && isInvalidNumber(azim),
    loadLoss: isInvalidPositiveNumber(loadLoss),
    noLoadLoss: isInvalidPositiveNumber(noLoadLoss),
    rated: !config.useRecommendedTransformer && isInvalidPositiveNumber(rated),
  };

  return {
    hasMissing:
      missing.tilt ||
      missing.azim ||
      missing.loadLoss ||
      missing.noLoadLoss ||
      missing.rated,
    missing,
  };
}

export default function PVSetup() {
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "info" });
  const { setConfig } = useEngine();
  const [showTransformerErrors, setShowTransformerErrors] = useState(false);

  useEffect(() => {
    const onTransformerValidationFailed = () => {
      setShowTransformerErrors(true);
      setSnack({
        open: true,
        msg: "Required parameters are missing (transformer fields are on the Grid & Interconnection page)",
        sev: "error",
      });
    };

    window.addEventListener(TRANSFORMER_EVENT, onTransformerValidationFailed);
    return () => window.removeEventListener(TRANSFORMER_EVENT, onTransformerValidationFailed);
  }, []);

  const handleSave = async (config) => {
    const { hasMissing } = validateTransformerConfig(config);

    if (hasMissing) {
      setShowTransformerErrors(true);
      setSnack({
        open: true,
        msg: "Required parameters are missing (transformer fields are on the Grid & Interconnection page)",
        sev: "error",
      });
      return;
    }

    if (!config?.hasTransformer) {
      setShowTransformerErrors(false);
    }

    setShowTransformerErrors(false);
    setConfig(config);
    setSnack({ open: true, msg: " PV Configuration saved \u0413\u0428\u0007", sev: "success" });
  };

  return (
    <>
      {/* No Grid wrapper restricting width */}
      <ConfigForm onSubmit={handleSave} submitLabel="Save PV" showTransformerErrors={showTransformerErrors} />

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snack.sev}
          variant="filled"
          sx={{ width: "100%", borderRadius: 2 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
